/* eslint-disable max-lines */
import { createApp, watch } from 'vue'

import App from './App.vue'
import { useNavStore } from './navigation/navStore'
import { registerShellNavigation } from './navigation/registerShellNavigation'
import { installPinia, installI18n, installSentry, recordSentryBreadcrumb } from './providers'
import { createAppRouter } from './router'
import { registerNavReset } from './shell/navResetRegistry'
import { registerQueryCallback } from './shell/queryCallbackRegistry'
import { registerAuthQueryCallback } from './shell/registerAuthQueryCallback'
import { registerFeatureLifecycles } from './shell/registerFeatureLifecycles'
import { registerReportQueryCallbacks } from './shell/registerReportQueryCallbacks'
import { registerSettingsQueryCallbacks } from './shell/registerSettingsQueryCallbacks'
import { installGetStartedSwitchLanding, revalidateBootRoute } from './shell/revalidateBootRoute'
import { installShellEventBus, resetShellEventBus, shellEventBus } from './shell/shellEventBus'
import { registerShellEventNames, SHELL_WORKSPACE_EVENTS } from './shell/shellEventRegistry'
import { isCompositionsLanding, useAccountBootstrapHooks } from './shell/useAccountBootstrapHooks'
import { installBootstrapCoordinator } from './shell/useBootstrap'
import { useExportTelemetry } from './shell/useExportTelemetry'

import {
  registerAccountQueryCallbacks,
  registerAccountSessionReset,
  useWorkspace,
} from '@/features/account'
import { registerAlertsLifecycle } from '@/features/alerts'
import {
  registerAnomalyDashboardWidget,
  registerAnomalyMetricIndexFallbackReporter,
  registerAnomalySessionReset,
} from '@/features/anomalies'
import {
  registerAudienceEventBus,
  registerAudiencePages,
  registerAudienceWidgets,
} from '@/features/audience'
import {
  useAuthStore,
  AuthLogoutFlow,
  AuthService,
  registerSessionReset,
  subscribeSessionReset,
} from '@/features/auth'
import { registerCommerceWidgets } from '@/features/commerce-analytics'
import {
  installCreativeAiSettings,
  installCreativeAiProgressLifecycle,
  registerCreativeAiEventBus,
} from '@/features/creative-ai'
import {
  installCreditsSettingsNav,
  registerBillingQueryCallbacks,
  useBillingReturnRuntime,
} from '@/features/credits'
import {
  registerDashboardBootstrapHydrator,
  registerDashboardNavReset,
} from '@/features/dashboards'
import { installConnectorsPane, registerDataConnectorNavResets } from '@/features/data-connectors'
import { registerDataExportLifecycle, registerDataExportNavResets } from '@/features/data-export'
import {
  registerGetStartedSettingsSurface,
  registerWhatsNewHydrator,
  registerOnboardingPlatformsHost,
  registerWorkspaceAttributeWriter,
  resolveShopifyOnboardingDestination,
} from '@/features/global-surface'
import { registerImpactEventBus } from '@/features/impact-analysis'
import { installInsightActions } from '@/features/insight-actions'
import {
  INSIGHTS_PREVIEW_ROUTE,
  installInsightsSettingsNav,
  openInsightFromQuery,
  registerInsightDashboardWidget,
  registerInsightNavReset,
  registerInsightSessionReset,
  registerInsightStatusWriteFailureReporter,
} from '@/features/insights'
import {
  registerIntegrationQueryCallbacks,
  installIntegrationsSettingsNav,
  registerIntegrationBootstrapHydrators,
  registerIntegrationsLifecycle,
  registerGetStartedProgressEventBus,
  useIntegrationCallbackRuntime,
} from '@/features/integrations'
import {
  registerMarketingInsightsNavResets,
  registerMarketingInsightsPages,
} from '@/features/marketing-insights'
import {
  installOrpheusSettings,
  registerOrpheusEventBus,
  registerOrpheusLifecycle,
  registerOrpheusTurnFailureReporter,
} from '@/features/orpheus'
import { registerPlatformActionSessionReset } from '@/features/platform-actions'
import {
  registerMetricCatalogSessionReset,
  registerReportBootstrapHydrators,
  registerReportNavResets,
} from '@/features/reports'
import {
  registerScorecardFetchFailureReporter,
  registerScorecardSessionReset,
} from '@/features/scorecards'
import { installWorkflowSettings, registerWorkflowSessionReset } from '@/features/workflow'
import {
  installDefinitionsSettings,
  registerDefinitionsSessionReset,
} from '@/features/workspace-definitions'
import {
  installGetStartedWorkspaceSettings,
  installWorkspaceSettingsPanes,
  registerSettingsChildRoutes,
  SaveAttributeFlow,
  type WorkspaceAttributeKey,
  type WorkspaceAttributeRegistry,
  registerSettingsNavEntry,
} from '@/features/workspace-settings'
import { validateEnv, type AppEnv } from '@/shared/config'
import { configureDateRange, useDateRangeStore } from '@/shared/date-range'
import { configureHttp } from '@/shared/http'
import { installIntercom } from '@/shared/lib/analytics/intercom'
import { configureExportTelemetry } from '@/shared/lib/export'
import { useToast } from '@/shared/ui'
import { useUiStore } from '@/shared/ui/store/uiStore'
import '@/styles/main.css'
// Self-hosted brand fonts (Urbanist = --ui-font-sans, Roboto = --ui-font-numeric). Imported
// here so Vite bundles the woff2 with correct hashed paths; @font-face registers globally.
import '@/assets/fonts/Urbanist/urbanist.css'
import '@/assets/fonts/Roboto/roboto.css'

/** Apply boot-owned document metadata without a hardcoded deployment origin. */
export function applyAppMetadata(env: Pick<AppEnv, 'APP_ICON' | 'APP_NAME'>): void {
  document.title = env.APP_NAME

  const existing = document.querySelector<HTMLLinkElement>('link[rel~="icon"]')
  const icon = existing ?? document.createElement('link')
  icon.rel = 'icon'
  icon.href = env.APP_ICON
  if (!existing) document.head.append(icon)
}

// The single ordered boot sequence (contract 01 §8, BRIEF §3.1). v3's boot logic was
// scattered across main.js + a 200-line router guard; here there is exactly one path.
// Later modules fill in the real steps (session boot, http config, i18n detection).
export async function bootstrap(): Promise<void> {
  // 1. Validate env first — throws EnvValidationError on a missing key (fail fast).
  const env = validateEnv()
  applyAppMetadata(env)

  // 2. Compose the app + providers (no global components — BRIEF §4.3). i18n is installed
  // after Pinia (it reads `localeStore`) and awaited: `installI18n` runs the locale-detection
  // fallback chain (`detectInitialLocale`) and loads the resolved locale's bundle so the first
  // paint has copy in the right language before the router mounts (contract 04 §8).
  const app = createApp(App)
  installPinia(app)
  const workspace = useWorkspace()
  configureDateRange({
    getWorkspaceDefault: () => workspace.getAttribute('ws_selection_default_date')?.value ?? null,
  })
  watch(
    workspace.workspaceId,
    (next, previous) => {
      if (previous !== undefined && next !== previous) useDateRangeStore().resetState()
    },
    { flush: 'sync' },
  )
  // Event NAMES are composition metadata: register them before the runtime install seals the
  // catalog. Their payloads remain unknown here; each consuming feature owns its parser.
  registerShellEventNames(SHELL_WORKSPACE_EVENTS)
  // Module 13 consumes the app-owned stream through a structural DI seam; it never imports app.
  registerImpactEventBus(shellEventBus)
  registerAudienceEventBus(shellEventBus)
  registerGetStartedProgressEventBus(shellEventBus)
  registerCreativeAiEventBus(shellEventBus)
  registerOrpheusEventBus(shellEventBus)
  installInsightActions(shellEventBus, { insightRouteName: INSIGHTS_PREVIEW_ROUTE.name })
  // Owning features register their bootstrap writers before account boot can dispatch data.
  registerReportBootstrapHydrators()
  registerIntegrationBootstrapHydrators()
  registerDashboardBootstrapHydrator()
  registerWhatsNewHydrator()
  // 29's onboarding writes four workspace attributes but may not import 22 (contract 29 §4/§15.2);
  // 22 owns the verb rule and the envelope. Only this file knows both sides — the same inversion
  // `installGetStartedWorkspaceSettings` uses below, in the opposite direction.
  // 29's connect step lists 12's platforms and opens 30's follow-ups; only this file knows both.
  registerOnboardingPlatformsHost(() => import('./shell/components/OnboardingPlatformsHost.vue'))
  registerWorkspaceAttributeWriter(async ({ key, value, attributeId, workspaceId }) => {
    await new SaveAttributeFlow().save(
      key as WorkspaceAttributeKey,
      value as WorkspaceAttributeRegistry[WorkspaceAttributeKey],
      { attributeId, ...(workspaceId === undefined ? {} : { workspaceId }) },
    )
  })
  registerInsightDashboardWidget()
  registerAnomalyDashboardWidget()
  // Legacy `router/index.js:51` cleared the transient dashboard-workspace pin at the top of the
  // god-guard; 09 contributes it and the shell runs it on every confirmed navigation.
  registerDashboardNavReset(registerNavReset)
  // Both nav resets go through the afterEach registry, never a beforeEach: `cancelInFlightGuard`
  // must abort before anything clears (v3 router/index.js:64-73 order, ORP-18644 AC6).
  registerReportNavResets(registerNavReset)
  // B23 keeps the data cache warm; only the transient shell panel closes on navigation.
  registerInsightNavReset(registerNavReset)
  // 16's wizard draft outlives its host (it is store state), so leaving the page must drop it.
  // Jobs/limits/templates deliberately survive — only the workspace/session edges clear those.
  registerDataExportNavResets(registerNavReset)
  // 20's connector wizard is the same shape as 16's and gets the same seam: the draft goes, the
  // scope-owned upload types and executions stay.
  registerDataConnectorNavResets(registerNavReset)
  // 25's retired Meta/TikTok explorer redirect (ORP-18435): a visitor arriving from either legacy
  // path lands on Budget Allocation with that channel already selected. Contributed here rather
  // than inside the redirect because `router.resolve()` runs redirect functions for any href
  // computation, so the seed would fire without a navigation.
  registerMarketingInsightsNavResets(registerNavReset)
  registerAccountSessionReset()
  registerAlertsLifecycle()
  registerAnomalySessionReset()
  // Audience .. Marketing Insights own their workspace/session resets in one call (ORP-22678).
  registerFeatureLifecycles()
  registerInsightSessionReset()
  registerInsightStatusWriteFailureReporter((failure) => {
    console.warn('[orphex] insight status write failed', failure)
  })
  registerAnomalyMetricIndexFallbackReporter((event) => {
    recordSentryBreadcrumb('anomalies.metric_index_fallback', 'Metric index fallback', {
      record_id: event.recordId,
      requested_metric: event.requestedMetric,
      fallback_metric: event.fallbackMetric,
    })
  })
  registerMetricCatalogSessionReset()
  // 16's jobs/limits/templates AND its wizard draft are workspace-scoped; both edges clear them.
  registerDataExportLifecycle()
  // 12's hydrators (above) reset the integration store on both edges; this closes the connector and
  // reauth dialogs that were open over it, which nothing did before (ORP-20384).
  registerIntegrationsLifecycle()
  registerOrpheusLifecycle()
  registerOrpheusTurnFailureReporter((failure) => {
    recordSentryBreadcrumb('orpheus.turn_failed', 'Orpheus turn failed', {
      state: failure.state,
      turn_id: failure.turnId,
      workspace_id: String(failure.workspaceId),
      kind: failure.kind,
      status: failure.status ?? '',
      code: failure.code ?? '',
    })
  })
  registerPlatformActionSessionReset()
  registerScorecardSessionReset()
  registerWorkflowSessionReset()
  registerDefinitionsSessionReset()
  registerScorecardFetchFailureReporter((failure) => {
    recordSentryBreadcrumb('scorecards.fetch_failed', 'Scorecard target list fetch failed', {
      kind: failure.kind,
      status: failure.status ?? '',
      code: failure.code ?? '',
    })
  })
  registerSessionReset(() => useNavStore().reset())
  registerSessionReset(() => useUiStore().resetTransientUi())
  registerSessionReset(() => useDateRangeStore().resetState())
  registerSessionReset(resetShellEventBus)
  // 27's dashboard-widget loader (09 §8 registry inversion) — registered before the router
  // mounts, like the hydrators above; the chunk itself loads only when a dashboard renders it.
  registerCommerceWidgets()
  registerAudiencePages()
  registerMarketingInsightsPages()
  registerAudienceWidgets()
  registerShellNavigation()
  // The export adapter's `download_triggered` sink (ORP-20213). Only the composition root can
  // hold both the production/GTM gate and module 07's user id, so the leaf takes it injected.
  configureExportTelemetry(useExportTelemetry())
  const i18n = await installI18n(app)
  registerAuthQueryCallback(i18n.global.t)
  registerIntegrationQueryCallbacks(
    registerQueryCallback,
    useIntegrationCallbackRuntime((key, params) => i18n.global.t(key, params ?? {})),
    { resolveShopifyOnboardingDestination },
  )
  registerBillingQueryCallbacks(
    registerQueryCallback,
    useBillingReturnRuntime((key) => i18n.global.t(key)),
  )
  // After auth's handoff: v3 swapped the token before selecting the account it named
  // (`router/index.js:210-222`), and the seam awaits handlers in registration order. Placed BEFORE
  // the report callback because v3 ran `?account_id`/`?workspace_id` first (`:145-166`) and
  // `?report_id` (`:167`) short-circuits with a redirect. One deviation: v3 evaluated `?account`
  // after that redirect, so a URL carrying both landed on the report in the OLD account; here the
  // account the URL names wins first.
  registerAccountQueryCallbacks(registerQueryCallback)
  // After auth's handoff: a simultaneous handoff+report_id exchanges the token first (v3 order).
  registerReportQueryCallbacks()
  registerQueryCallback('insight_id', ({ value }) => openInsightFromQuery(value))

  // Settings registrants attach HERE — before `createAppRouter()`, which seals module 22's
  // child-route registry (contract 22 §11, ledger §A14). Each owner installs its own entries and
  // route records through 22's two register functions; neither side imports the other. This is the
  // ONLY place that knows both 22 and its registrants exist. Adding a call after this point throws
  // — see `registerSettingsChildRoutes`.
  const settingsRegistrar = {
    registerNav: registerSettingsNavEntry,
    registerRoutes: registerSettingsChildRoutes,
  }
  installIntegrationsSettingsNav(settingsRegistrar, {
    // The app composition root owns the only boundary-safe bridge between 12's typed event and
    // 30's follow-up settings surfaces. Neither feature imports the other at runtime.
    connectionsComponent: () => import('./shell/components/ConnectionsSettingsHost.vue'),
  }) // 12 — ORP-18893 precondition 5, first half
  installCreditsSettingsNav(settingsRegistrar) // 11 — ORP-18893 precondition 5, second half
  installDefinitionsSettings(settingsRegistrar) // 30 — five workspace-definition panes
  installCreativeAiSettings(settingsRegistrar) // 23 — label-schema authoring + re-label lifecycle
  installConnectorsPane(settingsRegistrar) // 20 — the Workspace › Data Connectors pane (v3 parity)
  installOrpheusSettings(settingsRegistrar) // 18 — the account's Orpheus consent switch
  installWorkflowSettings(settingsRegistrar) // 14 — Workspace › Business Context (weekly digest)
  // 15 — ORP-20405, the SYSADMIN half only (workbench / config / authoring). The consumer Insights
  // Center is not routed through here; it contributes a shell sidebar leaf instead
  // (`features/insights/nav.ts`, ORP-20390).
  installInsightsSettingsNav(settingsRegistrar)
  installGetStartedWorkspaceSettings(registerGetStartedSettingsSurface)
  // 22's OWN panes go through the same door: their routes are static (`workspaceSettingsRoutes`),
  // but their nav entries must be registered by a call, never by a module side effect.
  installWorkspaceSettingsPanes()

  // AFTER the installers above: the `?selected_tab` shim resolves a legacy tab name against the
  // live nav registry, so every pane that will ever answer one must already be registered
  // (ORP-19899). Registration order among callbacks is load-bearing but this one never competes —
  // it owns a param no other handler reads.
  registerSettingsQueryCallbacks()

  const router = createAppRouter()
  const navStore = useNavStore()
  router.afterEach((to, from) => navStore.syncOnNavigation(to, from))

  // 2b. Wire auth into http — the dependency inversion (ledger A1): shared/http reads the
  // token + logout decision through injected accessors, so it never imports features/auth.
  // The bearer is restored here too (secure-ls-persisted authStore hydrates on first use).
  const session = useAuthStore()
  const navigateByName = (name: string, next?: string): void => {
    try {
      void router.replace(next ? { name, query: { next } } : { name })
    } catch {
      /* target route not registered yet (pre-cutover) — degrade to the catch-all */
    }
  }
  // A dead token fails every in-flight request at once; one logout serves the whole burst.
  let unauthorizedLogout: Promise<void> | null = null
  configureHttp({
    getToken: () => session.getToken(),
    onUnauthorized: () => {
      unauthorizedLogout ??= new AuthLogoutFlow({ navigate: navigateByName })
        .execute()
        .finally(() => {
          unauthorizedLogout = null
        })
    },
    registerAbort: (abort) => session.registerAbort(abort),
    onError: (error) => {
      useToast().error(i18n.global.t(error.i18nKey))
    },
  })
  // HTTP auth must be wired before the shell can open an authenticated workspace stream. The
  // runtime watches account `status` + `workspaceId` and leases the transport from live feature
  // subscribers, so non-consuming surfaces never create an idle EventSource.
  installShellEventBus()
  installCreativeAiProgressLifecycle()
  // Cross-tab logout: another tab's reset drives the same idempotent local teardown here.
  subscribeSessionReset(() => {
    new AuthLogoutFlow({ navigate: navigateByName }).resetSession()
  })

  // Start the shell-owned account boot before router installation triggers the first navigation.
  // The guard preserves pre-ready targets; this callback revalidates hydrated RBAC before the
  // coordinator opens AppLayout's route-render gate.
  installBootstrapCoordinator({
    navigate: navigateByName,
    // 07 owns the trigger for its post-boot side-effects but implements none of them; without a
    // consumer here every hook is dead code (ORP-20381). Only this file may hold both the flow's
    // seam and the modules that answer it.
    accountHooks: useAccountBootstrapHooks({
      navigate: (target) => router.replace(target),
      loadPendingInvites: () => new AuthService().getPendingInvites(),
    }),
    isCompositionsRoute: isCompositionsLanding,
    onReady: () => revalidateBootRoute(router),
  })
  installGetStartedSwitchLanding(router)
  app.use(router)

  // 3. Last-resort fallback error hooks — used when Sentry isn't wired. Set BEFORE
  // installSentry so that, once a DSN exists, Sentry's own app.config.errorHandler takes
  // precedence (it captures); module 05 chains the two rather than clobbering.
  app.config.errorHandler = (err) => {
    console.error('[orphex] uncaught error:', err)
  }
  window.addEventListener('unhandledrejection', (event) => {
    console.error('[orphex] unhandled rejection:', event.reason)
  })

  // 4. Sentry last, so it owns error capture when a DSN is present (no-op otherwise).
  installSentry(app, env)

  // 4b. Intercom widget (v3 `main.js` `initIntercom()`) — a no-op without `INTERCOM_APP_ID`, same
  // gate shape as Sentry. `useAccountBootstrapHooks`'s `identify` hook pushes the visitor once
  // account boot resolves; `AuthLogoutFlow` tears it down on logout (module 05 §identify).
  installIntercom(env.INTERCOM_APP_ID)

  // 5. Mount once the first route is resolved.
  await router.isReady()
  app.mount('#app')
}
